import type { CSSProperties, ReactNode } from 'react';
import {
  getProductBrandingText,
  hexToHslComponents,
  type ProductBrandingConfig,
  type ProductLocale,
} from '@/modules/auth/config/productBranding';
import '@/modules/auth/styles/product-branded-auth.css';

interface ProductBrandedAuthLayoutProps {
  branding: ProductBrandingConfig;
  locale: ProductLocale;
  children: ReactNode;
}

export function ProductBrandedAuthLayout({
  branding,
  locale,
  children,
}: ProductBrandedAuthLayoutProps) {
  const copy = getProductBrandingText(branding, locale);
  const primaryHsl = hexToHslComponents(branding.primaryColor);

  const themeStyle = {
    '--primary': primaryHsl,
    '--ring': primaryHsl,
    '--secondary': hexToHslComponents(branding.secondaryColor),
    '--wared-primary': primaryHsl,
  } as CSSProperties;

  return (
    <div
      className="product-branded-auth relative min-h-screen overflow-x-hidden"
      style={{
        background: branding.backgroundStyle,
        backgroundAttachment: 'fixed',
        ...themeStyle,
      }}
      dir={locale === 'ar' ? 'rtl' : 'ltr'}
      lang={locale}
    >
      {/* Ambient glow orbs */}
      <div
        className="branded-ambient-orb -end-24 -top-24 h-[420px] w-[420px] opacity-70"
        style={{ background: `hsl(${primaryHsl} / 0.18)` }}
        aria-hidden
      />
      <div
        className="branded-ambient-orb -bottom-32 -start-20 h-[360px] w-[360px] opacity-50"
        style={{ background: `hsl(${primaryHsl} / 0.12)` }}
        aria-hidden
      />
      <div
        className="branded-ambient-orb start-1/2 top-1/2 h-[280px] w-[280px] -translate-x-1/2 -translate-y-1/2 opacity-30"
        style={{ background: `hsl(235 85% 72% / 0.1)` }}
        aria-hidden
      />

      <div className="relative z-10 grid min-h-screen grid-cols-1 lg:grid-cols-[48%_1fr]">
        {/* Brand panel */}
        <aside className="branded-hero-panel px-6 py-12 sm:px-10 lg:px-14 xl:px-20 lg:py-16">
          <div className="branded-hero-content animate-fade-in">
            <img src={branding.logoUrl} alt={copy.productName} className="branded-hero-logo" />

            <div className="branded-hero-copy">
              <h1 className="branded-hero-title">{copy.title}</h1>
              <p className="branded-hero-subtitle">{copy.subtitle}</p>
            </div>

            <ul className="branded-hero-benefits hidden sm:block">
              {copy.benefits.map((benefit) => (
                <li key={benefit} className="branded-benefit-item flex items-start gap-3">
                  <span className="branded-benefit-check mt-0.5" aria-hidden>
                    ✓
                  </span>
                  <span>{benefit}</span>
                </li>
              ))}
            </ul>
          </div>
        </aside>

        {/* Login panel */}
        <main className="flex flex-1 items-center justify-center px-4 py-8 sm:px-6 lg:px-10 xl:px-16 lg:py-16">
          <div className="w-full max-w-[460px] animate-slide-up">
            {/* Mobile logo + headline */}
            <div className="mb-8 flex flex-col items-center gap-4 text-center lg:hidden">
              <img
                src={branding.logoUrl}
                alt={copy.productName}
                className="h-10 w-auto object-contain"
              />
              <div className="space-y-2">
                <h1 className="branded-hero-title text-xl">{copy.title}</h1>
                <p className="branded-hero-subtitle text-sm">{copy.subtitle}</p>
              </div>
              <ul className="mt-2 w-full max-w-sm space-y-2.5 text-start">
                {copy.benefits.map((benefit) => (
                  <li
                    key={benefit}
                    className="branded-benefit-item flex items-start gap-2.5 text-sm"
                  >
                    <span className="branded-benefit-check mt-0.5" aria-hidden>
                      ✓
                    </span>
                    <span>{benefit}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="branded-glass-card">{children}</div>
          </div>
        </main>
      </div>
    </div>
  );
}
